import React, { createContext, useContext, useEffect, useState } from "react";
import { API_BASE_URL } from "../config/api";

const AuthContext = createContext(null);

const STORAGE_KEY = "auth_state";
const GUEST_KEY = "guestMode";
const LANGUAGE_KEY = "kimat-language";
const DEFAULT_LANGUAGE = "en";

const SUPPORTED_LANGUAGES = new Set(["en", "de", "fr", "es", "uk", "tr"]);

function normalizeLanguage(value) {
    const language = String(value || "").trim().toLowerCase();
    return SUPPORTED_LANGUAGES.has(language) ? language : DEFAULT_LANGUAGE;
}

function applyLanguage(language) {
    const normalized = normalizeLanguage(language);
    localStorage.setItem(LANGUAGE_KEY, normalized);

    // Damit ein vorhandener LanguageProvider sofort reagieren kann.
    window.dispatchEvent(
        new CustomEvent("kimat-language-changed", {
            detail: { language: normalized }
        })
    );

    return normalized;
}

function loadFromStorage() {
    try {
        const isGuest = localStorage.getItem(GUEST_KEY) === "true";

        if (isGuest) {
            applyLanguage(DEFAULT_LANGUAGE);

            return {
                isGuest: true,
                userName: localStorage.getItem("username") || "Gast",
                preferredLanguage: DEFAULT_LANGUAGE,
                roles: []
            };
        }

        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        const data = JSON.parse(raw);
        if (!data.accessToken || !data.expiresAt) return null;
        if (Date.now() > data.expiresAt) return null;

        const preferredLanguage = applyLanguage(data.preferredLanguage);

        return {
            ...data,
            preferredLanguage,
            isGuest: false
        };
    } catch {
        return null;
    }
}

function saveToStorage(state) {
    if (state?.isGuest) {
        localStorage.setItem(GUEST_KEY, "true");
        localStorage.setItem("username", state.userName || "Gast");
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem("roles");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        applyLanguage(DEFAULT_LANGUAGE);
        return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function AuthProvider({ children }) {
    const [state, setState] = useState(() => loadFromStorage());

    const isGuest = !!state?.isGuest;
    const isAuthenticated = isGuest || !!state?.accessToken;
    const roles = isGuest ? [] : state?.roles || [];
    const userName = state?.userName || null;
    const preferredLanguage = isGuest
        ? DEFAULT_LANGUAGE
        : normalizeLanguage(state?.preferredLanguage);

    const loginAsGuest = () => {
        const guestState = {
            isGuest: true,
            userName: "Gast",
            preferredLanguage: DEFAULT_LANGUAGE,
            roles: []
        };

        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem("roles");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");

        localStorage.setItem(GUEST_KEY, "true");
        localStorage.setItem("username", "Gast");
        applyLanguage(DEFAULT_LANGUAGE);

        setState(guestState);
    };

    const login = async (userNameValue, password, turnstileToken) => {
        const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                userName: userNameValue,
                password,
                turnstileToken
            })
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
            throw new Error(data.error || "Login fehlgeschlagen");
        }

        const expiresAt = new Date(data.expiresAt).getTime();
        const language = applyLanguage(data.preferredLanguage);

        const newState = {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            expiresAt,
            userName: data.userName,
            preferredLanguage: language,
            roles: data.roles || [],
            isGuest: false
        };

        localStorage.removeItem(GUEST_KEY);
        localStorage.setItem("username", data.userName || userNameValue);
        localStorage.setItem("roles", JSON.stringify(data.roles || []));
        localStorage.setItem("accessToken", data.accessToken);
        localStorage.setItem("refreshToken", data.refreshToken || "");

        setState(newState);
        saveToStorage(newState);

        return newState;
    };

    const updatePreferredLanguage = async (languageValue) => {
        if (isGuest || !state?.accessToken) {
            applyLanguage(DEFAULT_LANGUAGE);
            throw new Error("Die Sprachauswahl ist nur für angemeldete Benutzer verfügbar.");
        }

        const requestedLanguage = normalizeLanguage(languageValue);

        const res = await fetch(`${API_BASE_URL}/api/auth/profile/language`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${state.accessToken}`
            },
            body: JSON.stringify({
                preferredLanguage: requestedLanguage
            })
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
            throw new Error(data.error || "Sprache konnte nicht gespeichert werden.");
        }

        const savedLanguage = applyLanguage(
            data.preferredLanguage || requestedLanguage
        );

        const newState = {
            ...state,
            preferredLanguage: savedLanguage
        };

        setState(newState);
        saveToStorage(newState);

        return savedLanguage;
    };

    const refreshProfile = async () => {
        if (isGuest || !state?.accessToken) return null;

        const res = await fetch(`${API_BASE_URL}/api/auth/profile`, {
            headers: {
                Authorization: `Bearer ${state.accessToken}`
            }
        });

        if (!res.ok) {
            if (res.status === 401) logout();
            throw new Error("Profil konnte nicht geladen werden.");
        }

        const data = await res.json();
        const language = applyLanguage(data.preferredLanguage);

        const newState = {
            ...state,
            userName: data.userName || state.userName,
            roles: data.roles || state.roles || [],
            preferredLanguage: language
        };

        localStorage.setItem("username", newState.userName || "");
        localStorage.setItem("roles", JSON.stringify(newState.roles));

        setState(newState);
        saveToStorage(newState);

        return data;
    };

    const logout = () => {
        setState(null);
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(GUEST_KEY);
        localStorage.removeItem("username");
        localStorage.removeItem("roles");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        applyLanguage(DEFAULT_LANGUAGE);
        window.location.href = "/login";
    };

    const authFetch = async (url, options = {}) => {
        if (isGuest || !state?.accessToken) {
            throw new Error("Nicht eingeloggt oder Gastzugriff nicht erlaubt");
        }

        const headers = {
            ...(options.headers || {}),
            Authorization: `Bearer ${state.accessToken}`
        };

        const res = await fetch(url, { ...options, headers });

        if (res.status === 401) {
            logout();
        }

        return res;
    };

    useEffect(() => {
        if (isGuest) return;
        if (!state?.refreshToken || !state?.expiresAt) return;

        const interval = setInterval(async () => {
            const diff = state.expiresAt - Date.now();

            if (diff < 30_000) {
                try {
                    const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ refreshToken: state.refreshToken })
                    });

                    if (!res.ok) {
                        logout();
                        return;
                    }

                    const data = await res.json();
                    const expiresAt = new Date(data.expiresAt).getTime();
                    const language = applyLanguage(
                        data.preferredLanguage || state.preferredLanguage
                    );

                    const newState = {
                        ...state,
                        accessToken: data.accessToken,
                        refreshToken: data.refreshToken,
                        expiresAt,
                        preferredLanguage: language,
                        roles: data.roles || state.roles || [],
                        isGuest: false
                    };

                    localStorage.setItem("accessToken", data.accessToken);
                    localStorage.setItem("refreshToken", data.refreshToken || "");

                    setState(newState);
                    saveToStorage(newState);
                } catch {
                    logout();
                }
            }
        }, 10_000);

        return () => clearInterval(interval);
    }, [state, isGuest]);

    const value = {
        isAuthenticated,
        isGuest,
        userName,
        roles,
        preferredLanguage,
        accessToken: isGuest ? null : state?.accessToken || null,
        refreshToken: isGuest ? null : state?.refreshToken || null,
        login,
        loginAsGuest,
        logout,
        authFetch,
        refreshProfile,
        updatePreferredLanguage
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    return useContext(AuthContext);
}
